/**
 * Como a aba "Quem clicou" (C1–C4) fala. **Só formata** o que o servidor decidiu: a situação do
 * clique, a permanência, a confiança, o flag de suspeito e a região chegam prontos de
 * `redirector.whoClicked` / `deviceSummary` / `regionSummary` (invariante 3).
 */
import { formatNumber, formatPercent, type BadgeTone } from '@mgdlead/ui';

import type { RegionSummary, WhoClickedRow, WhoClickedSummary } from './who-clicked-api';

export type WhoClickedEntry = WhoClickedRow['entry'];

export const ENTRY_SPEC: Record<WhoClickedEntry, { label: string; tone: BadgeTone; hint: string }> =
  {
    JOINED: {
      label: 'Entrou',
      tone: 'success',
      hint: 'Uma entrada no grupo foi casada com este clique. O casamento é estimado: veja a confiança ao lado.',
    },
    PENDING: {
      label: 'Ainda aguardando',
      tone: 'info',
      hint: 'A janela de atribuição deste link ainda está aberta: a pessoa ainda pode entrar.',
    },
    NOT_JOINED: {
      label: 'Não entrou',
      tone: 'neutral',
      hint: 'A janela de atribuição venceu sem nenhuma entrada casada com este clique.',
    },
    NO_GROUP: {
      label: 'Sem grupo',
      tone: 'warning',
      hint: 'Nenhum grupo da campanha podia receber este clique quando ele chegou.',
    },
  };

const MIN = 1;
const HORA = 60 * MIN;
const DIA = 24 * HORA;

/** "3 dias", "5 horas", "12 minutos" — a partir dos minutos que o servidor mediu. */
export function minutesText(minutes: number): string {
  if (minutes < 1) return 'menos de um minuto';
  if (minutes < HORA) return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
  if (minutes < DIA) {
    const h = Math.floor(minutes / HORA);
    return h === 1 ? '1 hora' : `${h} horas`;
  }
  const d = Math.floor(minutes / DIA);
  return d === 1 ? '1 dia' : `${d} dias`;
}

const EXIT_TEXT = {
  self: 'saiu por conta própria',
  removed_by_admin: 'removido por um admin',
  unknown: 'saída sem causa conhecida',
} as const;

/**
 * A coluna "Ficou". `null` quando a pessoa não entrou — e aí a tela mostra "—", porque não há
 * permanência a medir (não é zero).
 */
export function stayText(stay: WhoClickedRow['stay']): { text: string; hint: string } | null {
  if (stay === null) return null;
  if (stay.state === 'IN_GROUP') {
    return { text: 'Ainda no grupo', hint: `Está no grupo há ${minutesText(stay.minutes)}.` };
  }
  if (stay.state === 'UNKNOWN') {
    return {
      text: 'Não sabemos',
      hint: 'Entrou, mas não temos mais o registro de quem está no grupo para dizer se continua.',
    };
  }
  return {
    text: stay.minutes === null ? 'Saiu' : minutesText(stay.minutes),
    hint: `Ficou ${stay.minutes === null ? 'um tempo desconhecido' : minutesText(stay.minutes)} e ${EXIT_TEXT[stay.exitCause]}.`,
  };
}

export function repeatText(n: number): string | null {
  if (n <= 0) return null;
  return n === 1 ? '+1 clique repetido' : `+${n} cliques repetidos`;
}

/**
 * A frase do topo da aba. Os números são do servidor; o texto só os junta.
 * Com zero cliques, a frase é outra (é "nenhum clique", não "0 entraram de 0").
 */
export function summarySentence(s: WhoClickedSummary): string {
  if (s.clicks === 0) return 'Nenhum clique neste período.';
  const partes = [
    `${formatNumber(s.clicks)} ${s.clicks === 1 ? 'clique' : 'cliques'}`,
    `${formatNumber(s.joined)} ${s.joined === 1 ? 'entrou' : 'entraram'}`,
  ];
  if (s.pending > 0) partes.push(`${formatNumber(s.pending)} ainda aguardando`);
  return `${partes.join(' · ')}.`;
}

/** "Só aparece quem teve o telefone identificado (61% das entradas)." */
export function coverageSentence(r: RegionSummary): string | null {
  if (r.coverage === null) return null;
  return `Só aparece quem teve o telefone identificado (${formatPercent(r.coverage, 0)} das entradas).`;
}

/**
 * O mapa do Brasil fica desligado até existir um asset geográfico licenciado (pendência 5 da
 * spec). Até lá, a seção mostra só a tabela por UF/DDD.
 */
export const REGION_MAP_ENABLED = false;
